import { useCallback, useEffect, useState } from 'react';
import { Stack } from '@koob/margaret';
import { cloneDeep, omit } from 'lodash';
import { useTranslation } from 'react-i18next';
import styled, { css } from 'styled-components';
import { Text } from 'ui';
import { formatCurrency, roomTags } from 'utils';
import DmcBookingHotelInfo from './DmcContractHotelInfo';
import DmcBookingHotelRoomsInfo from './DmcContractHotelRoomsInfo';

import { useBooking, useLanguage } from 'hooks';
import { useTripFilters } from '@koob/trip-designer';
import { useKoobStore } from '../../store';
import { useShallow } from 'zustand/react/shallow';
import { useParams, useNavigate } from 'react-router-dom';
import { useAsync } from 'react-async';
import { daysBetween } from '@koob/dates';
import { useDeepCompareEffect } from 'react-use';
import { getTravelerComposition } from 'providers/BookingProvider';
import {
  KoobAlert,
  KoobButton,
  KoobPlaceholder,
  PromotionTag,
  RequiredPromotionTag,
} from '@koob/ui';
import KoobLoaderText from '@koob/ui/src/components/KoobLoaderText';
import formatHotelRoomPossibilities from '@koob/checkout/src/utilities/formatHotelRoomPossibilities';
import CheckoutHotelRoom from '@koob/checkout/src/components/CheckoutHotelRoom';
import { getRoomsAvailability } from '../../api/node/hotels';
import { BenefitsKindEnum, SupplierEnum } from '@koob/enums';
import { getStayFlexibleRequirements, isSameBedComposition } from '@koob/utils';
import { StayFlexibleRequirements } from '@koob/booking';
import { KOEDIA } from 'constants';

const Content = styled.div`
  position: relative;
  width: 100%;
`;

const SectionTitle = styled(Text).attrs({ type: 'h2' })`
  font-weight: bold;
`;

export const CompositionBed = styled(Stack).attrs({
  alignY: 'center',
  gutterSize: 0.25,
})`
  font-size: 0.75rem;
  padding: ${({ theme }) => theme.spacing(0.25)};
  border: 1px solid ${({ theme }) => theme.separator};
  border-radius: ${({ theme }) => theme.borderRadius.extraLarge};
  margin-top: 5px;

  ${(props) =>
    props.greenOption &&
    css`
      background-color: #eef8f2;
      color: #73c992;
    `}

  ${(props) =>
    props.orangeOption &&
    css`
      background-color: #ffe3e3;
      color: #ea5d5d !important;
    `}
`;

const RoomComponent = ({
  numberOfNights,
  room,
  selectedPossibilityIndex,
  isSelected,
  bedIndex,
  updateSelectedRoom,
  setCurrentSelectedPossibilityIndex,
  selectedRoomsCount,
  bookedRoom,
}) => {
  const { t } = useTranslation('bookings');

  const currentPossibility =
    selectedPossibilityIndex === undefined
      ? room
      : (room?.variants?.find(
          (variant) => variant.originalIndex === selectedPossibilityIndex,
        ) ?? room);

  const currentBed = currentPossibility.bedChoices[bedIndex];

  // KD-3532 — the bed this card would submit is the one the booking already
  // holds: picking it is not a change, so the card says so instead of leaving
  // the user with a confirm button that never enables.
  const isBookedRoom = isSameBedComposition(
    { bedChoice: currentBed },
    bookedRoom,
  );

  const supplementTypes = ['supplement', 'supplement_percent'];
  const hasGlobalPromo = currentPossibility.bedChoices.some(
    (item) =>
      (item.totalPriceWithPromo !== item.totalPriceWithoutPromo &&
        !supplementTypes.includes(item?.promotionInfo?.kind)) ||
      item?.promotionInfo?.kind === BenefitsKindEnum.AMENITY ||
      item?.promotionInfo?.kind === BenefitsKindEnum.UPGRADE_ROOM,
  );
  const koediaPromos = currentPossibility?.bedChoices[0]?.promotions?.filter(
    (item) => Boolean(item),
  );

  const renderPromotions = () => {
    const allPromotions = currentBed?.promotions?.filter(Boolean) ?? [];
    const allSupplementsMandatory =
      currentPossibility.bedChoices[bedIndex]?.mandatorySupplements ?? [];
    const optionalSupplements =
      currentPossibility.bedChoices[bedIndex]?.optionalSupplements;
    const allSupplements = Object.entries(
      allSupplementsMandatory.reduce((acc, val) => {
        acc[val] = (acc[val] || 0) + 1;
        return acc;
      }, {}),
    ).map(([key, value]) => `${key} (x${value})`);
    // Its own supplements decide whether this room has anything to show — the
    // list the recap was built from belongs to the room being chosen, and gated
    // these tags on a room the user is not looking at.
    if (
      allPromotions.length > 0 ||
      allSupplements.length > 0 ||
      optionalSupplements?.length > 0
    ) {
      return (
        <>
          {hasGlobalPromo &&
            allPromotions.length > 0 &&
            (allPromotions || []).map((name, index) => (
              <PromotionTag key={index} name={name} />
            ))}

          {optionalSupplements?.map(({ name }, index) => (
            <PromotionTag key={index} name={name} />
          ))}

          {allSupplements.length > 0 &&
            allSupplements?.map((supplement, index) => (
              <RequiredPromotionTag
                key={index}
                name={supplement}
                label={t('requiredSupplement')}
              />
            ))}
        </>
      );
    }
    return null;
  };

  return (
    <CheckoutHotelRoom
      scope="dmc"
      room={currentPossibility}
      parentRoom={room}
      testId={`${currentPossibility?.name
        ?.toLowerCase()
        .split(' ')
        .join('-')}-${selectedRoomsCount}`}
      selectedRoomIndex={selectedPossibilityIndex}
      currentBed={currentBed}
      price={{
        total: currentBed?.totalPriceWithPromo,
        currency: currentBed?.currency,
        nightsCount: numberOfNights,
      }}
      isSelected={isSelected && !isBookedRoom}
      disabled={isBookedRoom}
      onSelect={(newIndex) => {
        // The click can come from the variants footer, which offers the OTHER
        // rooms grouped under this card: the bed to submit belongs to the
        // variant that was clicked, not to the one currently on display.
        const nextPossibility =
          room?.variants?.find(
            (variant) => variant.originalIndex === newIndex,
          ) ?? currentPossibility;

        updateSelectedRoom(newIndex, 0, nextPossibility);
        setCurrentSelectedPossibilityIndex(newIndex);
      }}
    >
      {isBookedRoom && (
        <KoobAlert variant="warning" icon="fa-triangle-exclamation">
          {t('sameRoomAsBooked')}
        </KoobAlert>
      )}

      <div className="flex flex-wrap gap-3">
        {renderPromotions()}

        {((room?.supplier === SupplierEnum.KOEDIA && koediaPromos) || []).map(
          (name, index) =>
            name !== null && <PromotionTag key={index} name={name} />,
        )}
      </div>
    </CheckoutHotelRoom>
  );
};

const SelectRoomsForm = ({
  onSelectHotelsRooms,
  hotel,
  setRequestId,
  setExpirationDate,
  newBedRoom,
  setNewBedRoom,
  bookedRoom,
  updateBookingFilters,
  isOtherProposal = false,
}) => {
  const { t } = useTranslation('bookings');
  const { lang } = useLanguage();
  const {
    filters: createBookingFilters,
    nationalities,
    replaceBookingId,
    setBedIndexes,
    bedIndexes,
  } = useBooking();
  const navigate = useNavigate();
  const filters = updateBookingFilters ?? createBookingFilters;
  const [selectedRooms, setSelectedRooms] = useState([]);
  const [currentSelectedRoomIndex, setCurrentSelectedRoomIndex] = useState(0);
  const [currentSelectedPossibilityIndex, setCurrentSelectedPossibilityIndex] =
    useState(0);
  const [currentSelectedBedIndex, setCurrentSelectedBedIndex] = useState(0);
  const { rooms = [] } = filters;
  const [possibilities, setPossibilities] = useState([]);
  const [initialPossibilities, setInitialPossibilities] = useState([]);
  const { hotelId } = useParams();
  const numberOfNights = daysBetween(filters.startAt, filters.endAt);
  const [optionalPromo, setOptionalPromo] = useState([]);
  const tripFilters = useTripFilters();
  const { basket, updateInBasket } = useKoobStore(
    useShallow((state) => ({
      basket: state.basket,
      updateInBasket: state.updateInBasket,
    })),
  );
  const [selectedPromo, setSelectedPromo] = useState();

  const basketHotel = basket?.find(
    (basketExperience) => basketExperience?.internalId === filters?.internalId,
  );

  const tagsKey = (tags) => {
    return tags
      ?.map((t) => t.label?.toLowerCase()?.replace(/ /g, ''))
      ?.sort()
      ?.join('#');
  };

  const getPromotionsKey = (promos = []) => {
    const sorted = promos
      .map((promotion) => promotion.toLowerCase().replace(/\s+/g, ''))
      .sort();

    return JSON.stringify(sorted);
  };

  useDeepCompareEffect(() => {
    const selectedBed = { [currentSelectedRoomIndex]: currentSelectedBedIndex };

    if (bedIndexes.length > 0) {
      const indexToUpdate = bedIndexes.findIndex((item) =>
        item.hasOwnProperty(currentSelectedRoomIndex),
      );

      if (indexToUpdate !== -1) {
        setBedIndexes((prev) => {
          const updatedBedIndexes = [...prev];
          updatedBedIndexes[indexToUpdate] = selectedBed;
          return updatedBedIndexes;
        });
      } else {
        setBedIndexes((prev) => [...prev, selectedBed]);
      }
    } else {
      setBedIndexes([selectedBed]);
    }
  }, [{ currentSelectedBedIndex, currentSelectedRoomIndex }]);

  const isLastRoomOnRequest =
    Number(rooms.length) - Number(selectedRooms.length) === 1
      ? possibilities[currentSelectedRoomIndex]?.instantAvailability
      : false;
  const isBookingOnRequest =
    selectedRooms.length > 0
      ? selectedRooms.some((room) => room?.instantAvailability)
      : possibilities[currentSelectedRoomIndex]?.instantAvailability;

  const currentSelectedRoom = initialPossibilities[currentSelectedRoomIndex];

  const { data, isLoading } = useAsync(
    {
      promiseFn: getRoomsAvailability,
      id: hotel?.id ?? hotelId,
      locale: lang,
      filters: {
        startAt: filters.startAt,
        endAt: filters.endAt,
        rooms: filters?.rooms?.map((room) => ({
          ...omit(room, 'children'),
          firstAdultNationality: room?.firstAdultNationality?.value,
        })),
        // KD-3815 preview: the TO's rooms, read-only; a trip or a replaced
        // booking is the caller's own (400 VIEW_AS_WITH_TRIP).
        viewAs: filters?.viewAs?.value,
        replaceBookingId: filters?.viewAs?.value
          ? undefined
          : (replaceBookingId ?? undefined),
        tripId: filters?.viewAs?.value ? undefined : filters.tripId,
      },
    },
    false,
  );

  const getRefundableTag = (tags) => {
    return (tags ?? []).find(
      (tag) =>
        tag.code === roomTags.REFUNDABLE ||
        tag.code === roomTags.NON_REFUNDABLE,
    )?.code;
  };

  const updateOriginalCompatibilityRoom = useCallback(
    (rooms, referencesRooms) => {
      rooms.flatMap((room) => {
        if (room.compatibleRoomCompositions?.length > 0) {
          updateOriginalCompatibilityRoom(
            room.compatibleRoomCompositions,
            referencesRooms,
          );
        }
        if (room.originalIndex === undefined) {
          if (room.supplier !== SupplierEnum.KOOB) {
            const originalRoom = referencesRooms.find((rRef) => {
              if (rRef.supplier === SupplierEnum.KOOB) {
                return false;
              }
              return (
                rRef.name === room.name &&
                rRef.supplier === room.supplier &&
                rRef.source === room.source &&
                getRefundableTag(rRef.bedChoices[0].tags) ===
                  getRefundableTag(room.bedChoices[0].tags)
              );
            });
            if (originalRoom) {
              room.originalIndex = originalRoom.originalIndex;
            }
          } else {
            const originalRoom = referencesRooms.find((rRef) => {
              if (rRef.supplier !== SupplierEnum.KOOB) {
                return false;
              }
              const rBedsIds = rRef.bedChoices
                .map((bed) => {
                  return `${bed.promotions.join('_')}`;
                })
                .join('_');
              const bedIds = room.bedChoices
                .map((bed) => {
                  return `${bed.promotions.join('_')}`;
                })
                .join('_');

              return (
                rRef.supplierSpecificInformations.hotelRoomId ===
                  room.supplierSpecificInformations.hotelRoomId &&
                rBedsIds === bedIds
              );
            });

            room.originalIndex = originalRoom?.originalIndex;
            room.allPromotions = originalRoom?.allPromotions;
          }
        }
        return {};
      });
    },
    [],
  );

  const areCompositionsEqual = useCallback((composition1, composition2) => {
    const isSameCompositions =
      composition1.name === composition2.name &&
      composition1.supplierCode === composition2.supplierCode &&
      composition1.source === composition2.source &&
      composition1.bedChoices[0].totalPriceWithPromo ===
        composition2.bedChoices[0].totalPriceWithPromo &&
      tagsKey(composition1.bedChoices[0].tags) ===
        tagsKey(composition2.bedChoices[0].tags) &&
      getPromotionsKey(composition1.bedChoices[0]?.promotions) ===
        getPromotionsKey(composition2.bedChoices[0]?.promotions);

    return isSameCompositions;
  }, []);

  const getRoomKey = useCallback((room, depth) => {
    return `${room.name}_${room.supplierCode || room.supplier}_${room.source}_${
      room.bedChoices[0].totalPriceWithPromo
    }_${depth}_${tagsKey(room.bedChoices[0].tags)}_${getPromotionsKey(room.bedChoices[0]?.promotions)}`;
  }, []);

  const mergeCompatibleRoomCompositions = useCallback(
    (targetRoom, compatibleRoomCompositions, depth, sourceBedId = null) => {
      targetRoom.compatibleRoomCompositions =
        targetRoom.compatibleRoomCompositions || [];
      (compatibleRoomCompositions || []).forEach((composition) => {
        const existingComposition = targetRoom.compatibleRoomCompositions.find(
          (existingComposition) =>
            areCompositionsEqual(existingComposition, composition),
        );
        if (!existingComposition) {
          targetRoom.compatibleRoomCompositions.push({
            ...composition,
            ...(sourceBedId ? { _sourceBedId: sourceBedId } : {}),
          });
        } else if (depth > 0) {
          mergeCompatibleRoomCompositions(
            existingComposition,
            composition.compatibleRoomCompositions,
            depth - 1,
            sourceBedId,
          );
        }
      });
    },
    [areCompositionsEqual],
  );

  const wrapAvailableRoomCompositions = useCallback(
    (mergedRooms, room, depth) => {
      const key = getRoomKey(room, depth);
      if (!mergedRooms.has(key)) {
        mergedRooms.set(key, { ...room });
      } else {
        mergeCompatibleRoomCompositions(
          mergedRooms.get(key),
          room.compatibleRoomCompositions,
          depth,
          room.bedChoices[0]?.id,
        );
      }
      if (depth > 0) {
        (room.compatibleRoomCompositions || []).forEach((composition) => {
          wrapAvailableRoomPossibilities(
            composition.compatibleRoomCompositions || [],
          );
        });
      }
    },
    // eslint-disable-next-line
    [mergeCompatibleRoomCompositions, getRoomKey],
  );

  const wrapAvailableRoomPossibilities = useCallback(
    (availableRooms) => {
      const mergedRooms = new Map();
      availableRooms.forEach((room) => {
        wrapAvailableRoomCompositions(mergedRooms, room, rooms?.length - 1);
      });
      return Array.from(mergedRooms.values());
    },
    [wrapAvailableRoomCompositions, rooms?.length],
  );

  const removeDuplicateRooms = (rooms) => {
    const uniqueRoomsIndex = {};
    const uniqueRooms = rooms.filter((room) => {
      const key = `${room.name}_${
        room?.supplierCode ? room?.supplierCode : room?.supplier
      }_${room.source}_${room.bedChoices[0].totalPriceWithPromo}_${tagsKey(
        room.bedChoices[0].tags,
      )}_${getPromotionsKey(room.bedChoices[0]?.promotions)}`;

      if (!uniqueRoomsIndex[key]) {
        uniqueRoomsIndex[key] = true;
        return true;
      }
      return false;
    });
    return uniqueRooms;
  };

  const groupPossibilities = useCallback(
    (data) => {
      const filteredPossibilities = [];
      const datas = cloneDeep(data.possibilities);

      const possibilitiesData = datas.sort((a, b) => {
        const hasPromoA = +a.bedChoices?.some((bc) => bc.promotions.length);
        const hasPromoB = +b.bedChoices?.some((bc) => bc.promotions.length);
        return hasPromoB - hasPromoA;
      });

      const originalPossibilities = possibilitiesData.map(
        (possibility, index) => {
          possibility.originalIndex = index;
          if (possibility.supplier === SupplierEnum.KOOB) {
            filteredPossibilities.push(possibility);
          }

          return possibility;
        },
      );

      const groupedPossibilities = {};

      filteredPossibilities.forEach((possibility) => {
        const key = `${possibility?.supplierSpecificInformations?.hotelRoomId}`;

        if (!groupedPossibilities[key]) {
          groupedPossibilities[key] = {
            ...possibility,
            allPromotions: {},
            originalIndexes: [],
          };
        }

        possibility.bedChoices.forEach((bedChoice) => {
          if (
            bedChoice.promotionInfo?.kind !== 'supplement' &&
            bedChoice.promotionInfo?.kind !== 'supplement_percent'
          ) {
            // Prefix the promo id so the outer key is not an integer-like
            // string, otherwise Object.values() would auto-sort numerically
            // and break the API ordering (which puts cumulative combos first).
            const promoKey = `p${bedChoice.id.split('##').pop()}`;
            if (!groupedPossibilities[key].allPromotions[promoKey]) {
              groupedPossibilities[key].allPromotions[promoKey] = [
                { [bedChoice.id]: bedChoice.promotions },
              ];
            } else {
              groupedPossibilities[key].allPromotions[promoKey].push({
                [bedChoice.id]: bedChoice.promotions,
              });
            }
            if (
              groupedPossibilities[key].originalIndexes.indexOf(
                possibility.originalIndex,
              ) === -1
            ) {
              groupedPossibilities[key].originalIndexes.push(
                possibility.originalIndex,
              );
            }
          }
        });
      });
      const newPossibilities = Object.values(groupedPossibilities).map(
        (possibility) => {
          const allPromotions = Object.values(possibility.allPromotions).flat();
          delete possibility.allPromotions;
          return { ...possibility, allPromotions };
        },
      );

      newPossibilities.forEach((possibility) => {
        originalPossibilities.forEach((originalPossibility) => {
          if (
            true ===
            possibility?.originalIndexes?.includes(
              originalPossibility.originalIndex,
            )
          )
            originalPossibility.allPromotions = possibility.allPromotions;
        });
      });

      const remainingPossibilitiesKoedia = originalPossibilities.filter(
        (possibility) => possibility.supplier === SupplierEnum.KOEDIA,
      );

      const remainingPossibilitiesRakuten = originalPossibilities.filter(
        (possibility) => possibility.supplier === SupplierEnum.RAKUTEN,
      );
      const remainingPossibilitiesStaah = originalPossibilities.filter(
        (possibility) => possibility.supplier === SupplierEnum.STAAH,
      );
      // Channel-manager rooms (flat list, no compatible compositions), same
      // handling as Staah.
      const remainingPossibilitiesTlLincoln = originalPossibilities.filter(
        (possibility) => possibility.supplier === SupplierEnum.TL_LINCOLN,
      );

      const wrappedRemainingPossibilitiesKoedia =
        wrapAvailableRoomPossibilities(remainingPossibilitiesKoedia);

      updateOriginalCompatibilityRoom(
        originalPossibilities,
        originalPossibilities,
      );

      const newOriginalPossibilities = [
        ...newPossibilities,
        ...wrappedRemainingPossibilitiesKoedia,
        ...remainingPossibilitiesRakuten,
        ...remainingPossibilitiesStaah,
        ...remainingPossibilitiesTlLincoln,
      ].sort((a, b) => a.originalIndex - b.originalIndex);

      const supplementTypes = ['supplement', 'supplement_percent'];
      const updatedPossibiliiesList = newOriginalPossibilities.map((item) => {
        const composition = item;
        composition?.bedChoices.forEach((bedChoice) => {
          if (supplementTypes.includes(bedChoice?.promotionInfo?.kind)) {
            bedChoice.totalPriceWithPromo = bedChoice.totalPriceWithoutPromo;
          }
        });
        return composition;
      });

      const cleanedPossibilities = removeDuplicateRooms(
        updatedPossibiliiesList,
      );
      return { original: originalPossibilities, new: cleanedPossibilities };
    },
    [
      updateOriginalCompatibilityRoom,
      wrapAvailableRoomPossibilities,
      selectedRooms,
    ],
  );

  // KD-3496 — the optional supplements of the rooms already validated, summed in
  // both currencies: the total is shown in USD as well as in the supplier's
  // currency, and quoting one of them without the supplements makes the two
  // figures disagree with the room cards (which do add them).
  const sumOptionalPromos = (rooms, key) =>
    (rooms || []).reduce(
      (total, room) =>
        total +
        (room?.optionalPromos?.reduce(
          (promoTotal, promo) => promoTotal + (promo?.[key] ?? 0),
          0,
        ) || 0),
      0,
    );

  const selectedRoomTotalPrice = sumOptionalPromos(selectedRooms, 'price');
  const selectedRoomTotalPriceInDollar = sumOptionalPromos(
    selectedRooms,
    'priceInDollar',
  );

  const formatDataBasedOnSelection = (selectedRooms, possibility) => {
    let formattedData = {};

    for (let i = 0; i < selectedRooms.length; i++) {
      if (!possibility) {
        return formattedData;
      }
      formattedData = {
        possibilities: possibility?.compatibleRoomCompositions,
      };
    }

    return formattedData;
  };

  useEffect(() => {
    // Get default selected room for the other proposal
    if (!isOtherProposal || newBedRoom) return;

    const currentPossibility = initialPossibilities[currentSelectedRoomIndex];
    if (!currentPossibility) return;

    setNewBedRoom({
      name: currentPossibility.name,
      bedChoice: currentPossibility.bedChoices[0],
      otherProposal: true,
    });
  }, [isOtherProposal, initialPossibilities, currentSelectedRoomIndex]);

  useEffect(() => {
    const canResetIndex =
      selectedRooms?.[0]?.supplier === SupplierEnum.KOEDIA &&
      currentSelectedRoomIndex > initialPossibilities.length;
    if (canResetIndex) {
      setCurrentSelectedRoomIndex(0);
    }
  }, [initialPossibilities, currentSelectedRoomIndex, selectedRooms]);

  useEffect(() => {
    const isKoediaSupplier =
      selectedRooms?.[0]?.supplier === SupplierEnum.KOEDIA;
    const isRakutenSupplier =
      selectedRooms?.[0]?.supplier === SupplierEnum.RAKUTEN;
    const isStaahSupplier =
      selectedRooms?.[0]?.supplier === SupplierEnum.STAAH ||
      selectedRooms?.[0]?.supplier === SupplierEnum.TL_LINCOLN;
    // Own inventory (KOOB) no longer returns compatibleRoomCompositions; the
    // next room's options come from the same flat list, filtered by capacity at
    // render time, so there's nothing to re-derive on each selection.
    const isOwnInventory = selectedRooms?.[0]?.supplier === SupplierEnum.KOOB;

    if (data && data.possibilities && selectedRooms.length === 0) {
      const response = cloneDeep(data);
      const groupedPossibilities = groupPossibilities(response);

      setPossibilities(groupedPossibilities.new);
      const initialPossibilitiesSelected = isKoediaSupplier
        ? groupedPossibilities.new
        : groupedPossibilities.original;
      setInitialPossibilities(initialPossibilitiesSelected);
      setRequestId(data.requestId);
      setExpirationDate(data.expiresAt);
    }

    const formatDataInput = isKoediaSupplier ? possibilities : selectedRooms;

    if (selectedRooms.length > 0 && !isOwnInventory) {
      const result =
        isKoediaSupplier || isRakutenSupplier || isStaahSupplier
          ? isKoediaSupplier
            ? formatDataInput
            : possibilities
          : cloneDeep(data?.possibilities);

      const possibility = result?.find((item) => {
        const lastSelectedRoom = selectedRooms.at(-1);

        return (
          item.name === lastSelectedRoom.name &&
          (!isKoediaSupplier ||
            (tagsKey(item.bedChoices[0].tags) ===
              tagsKey(lastSelectedRoom.bedChoices[0].tags) &&
              getPromotionsKey(item.bedChoices[0].promotions) ===
                getPromotionsKey(lastSelectedRoom.bedChoices[0].promotions)))
        );
      });

      if (possibility) {
        const updatedPossibilities = formatDataBasedOnSelection(
          formatDataInput,
          possibility,
        );
        if (updatedPossibilities?.possibilities.length > 0) {
          const groupedPossibilities = groupPossibilities(updatedPossibilities);
          setPossibilities(groupedPossibilities.new);
          setInitialPossibilities(groupedPossibilities.original);
        }
        setRequestId(data.requestId);
        setExpirationDate(data.expiresAt);
      }
    }
    // eslint-disable-next-line
  }, [
    data,
    setRequestId,
    setExpirationDate,
    groupPossibilities,
    selectedRooms,
  ]);

  function correctRoomsForKoedia(previousRooms, lastRoom) {
    const isKoediaSupplier =
      (previousRooms[0]?.supplier ?? lastRoom.supplier)?.toUpperCase() ===
      KOEDIA;
    if (!isKoediaSupplier || previousRooms.length === 0) {
      return previousRooms;
    }

    const allRooms = [
      ...previousRooms.map((r) => ({ ...r, bed: { ...r.bed } })),
      lastRoom,
    ];

    for (let i = allRooms.length - 2; i >= 0; i--) {
      const nextRoom = allRooms[i + 1];
      const compatEntry = allRooms[i].compatibleRoomCompositions?.find(
        (entry) => entry.bedChoices?.[0]?.id === nextRoom.bedCompositionId,
      );
      if (compatEntry?._sourceBedId) {
        allRooms[i] = {
          ...allRooms[i],
          bed: { ...allRooms[i].bed, id: compatEntry._sourceBedId },
          bedCompositionId: compatEntry._sourceBedId,
        };
      }
    }

    return allRooms.slice(0, -1);
  }

  const getCurrentSelectedRoom = () => {
    const bed =
      initialPossibilities[currentSelectedRoomIndex].bedChoices[
        currentSelectedBedIndex
      ];

    return {
      ...initialPossibilities[currentSelectedRoomIndex],
      bed: bed,
      roomIndex: currentSelectedRoomIndex,
      bedIndex: currentSelectedBedIndex,
      firstAdultNationality:
        filters?.rooms[selectedRooms.length].firstAdultNationality,
      adults: filters?.rooms[selectedRooms.length].adults,
      children: (filters?.rooms[selectedRooms.length].childrenBirthdates ?? [])
        .length,
      bedCompositionId: bed?.id,
      promotionId: selectedPromo?.promotionId,
      optionalPromos: optionalPromo,
    };
  };
  const validateRoom = () => {
    const currentSelectedRoom = getCurrentSelectedRoom();
    if (rooms?.length !== selectedRooms.length + 1) {
      setSelectedRooms((prev) => [...prev, currentSelectedRoom]);
      // Don't carry the just-confirmed room over as the next slot's tentative
      // selection — it may not fit the next room's occupancy (and would then
      // show in the recap while being absent from the list). Reset so the user
      // explicitly picks from that slot's valid list (matches the TO app).
      setCurrentSelectedRoomIndex(undefined);
      setCurrentSelectedPossibilityIndex(undefined);
      setCurrentSelectedBedIndex(0);
      // KD-3496 — the confirmed room keeps its own `optionalPromos`; the shared
      // selection has to start empty for the next slot. Left as it was, the
      // next room inherited it through getCurrentSelectedRoom(), was priced for
      // it, and was warned about an extra night nobody asked for.
      setOptionalPromo([]);
      return;
    }

    const correctedRooms = correctRoomsForKoedia(
      selectedRooms,
      currentSelectedRoom,
    );
    onSelectHotelsRooms([...correctedRooms, currentSelectedRoom]);
  };

  const handleChooseRoom = () => {
    const currentSelectedRoom = getCurrentSelectedRoom();
    const travelers = getTravelerComposition({
      compositions: [
        {
          adults:
            basketHotel.filters.experienceComposition[
              basketHotel?.selectedRooms?.length ?? 0
            ].adults,
          children:
            basketHotel.filters.experienceComposition[
              basketHotel?.selectedRooms?.length ?? 0
            ].childrenBirthdates.length,
        },
      ],
      customerFile: basketHotel.clientFolder,
      nationalities: nationalities,
    })[0].travelers;

    const newRoom = { ...currentSelectedRoom, travelers };
    const isLastRoom =
      (basketHotel?.selectedRooms?.length
        ? basketHotel?.selectedRooms?.length + 1
        : 1) === basketHotel.filters?.experienceComposition?.length;

    const previousRooms = basketHotel.selectedRooms ?? [];
    const correctedPreviousRooms = isLastRoom
      ? correctRoomsForKoedia(previousRooms, newRoom)
      : previousRooms;

    updateInBasket({
      ...basketHotel,
      selectedRooms: [...correctedPreviousRooms, newRoom],
      newRoom: true,
      requestId: data.requestId,
    });
    // Same as validateRoom: the room just pushed to the basket owns its
    // supplements now, and the next slot has to start from nothing.
    setOptionalPromo([]);
    if (isLastRoom) {
      navigate(`/bookings/trips/${tripFilters?.tripId}/checkout`, {
        replace: true,
      });
    }
  };

  const handleProposalRoom = (bedIndex, possibility) => {
    if (!isOtherProposal) return;

    setNewBedRoom({
      name: possibility.name,
      bedChoice: possibility.bedChoices[bedIndex],
      otherProposal: true,
    });
  };

  const handleRoomIndexChange = (index, bedIndex, possibility) => {
    setCurrentSelectedRoomIndex(index);
    setCurrentSelectedBedIndex(bedIndex);
    setBedIndexes([]);

    if (isOtherProposal) {
      setNewBedRoom({
        name: possibility.name,
        bedChoice: possibility.bedChoices[bedIndex],
        otherProposal: true,
      });
    }
  };

  let totalPrice = 0;
  let totalPriceInDollar = 0;
  selectedRooms.forEach((room) => {
    totalPrice += parseFloat(room?.bed?.totalPriceWithPromo);
    totalPriceInDollar += parseFloat(room?.bed?.totalPriceInDollar?.withPromo);
  });
  totalPrice += parseFloat(
    initialPossibilities[currentSelectedRoomIndex]?.bedChoices[
      currentSelectedBedIndex
    ].totalPriceWithPromo ?? 0,
  );

  const currency =
    initialPossibilities[currentSelectedRoomIndex]?.bedChoices[
      currentSelectedBedIndex
    ]?.currency ?? selectedRooms.at(-1)?.bed?.currency;

  totalPriceInDollar += parseFloat(
    initialPossibilities[currentSelectedRoomIndex]?.bedChoices[
      currentSelectedBedIndex
    ].totalPriceInDollar?.withPromo ?? 0,
  );

  const prices =
    selectedRoomTotalPrice +
    optionalPromo.reduce((total, { price }) => total + (price ?? 0), 0);
  totalPrice += parseFloat(prices);

  const pricesInDollar =
    selectedRoomTotalPriceInDollar +
    optionalPromo.reduce(
      (total, { priceInDollar }) => total + (priceInDollar ?? 0),
      0,
    );
  totalPriceInDollar += parseFloat(pricesInDollar);

  const roomsForRecap = () => {
    const matchingRoom = initialPossibilities[currentSelectedRoomIndex];

    return [
      ...selectedRooms,
      // Only show a tentative current room once one is actually selected for
      // this slot; otherwise the recap would render an empty/stale card.
      ...(matchingRoom
        ? [
            {
              ...matchingRoom,
              bed: matchingRoom.bedChoices[currentSelectedBedIndex ?? 0],
            },
          ]
        : []),
    ];
  };

  // KD-3496 — a validated room keeps its own `optionalPromos`, and the select
  // reads them back. Without writing the change into that stored entry the
  // input is frozen: `selectedRooms[i].optionalPromos ?? optionalPromo` stops
  // falling through as soon as the room holds an array (even an empty one), so
  // ticking an option changed nothing on screen and never reached the payload.
  // The price moves by the DELTA, which stays right whatever was ticked before
  // and is a no-op for a zero-priced early check-in.
  const updateRoomSupplements = (roomIndex, supplements) => {
    setSelectedRooms((previousRooms) =>
      previousRooms.map((room, index) => {
        if (index !== roomIndex) {
          return room;
        }
        const sum = (list, key) =>
          (list ?? []).reduce((total, promo) => total + (promo?.[key] ?? 0), 0);
        const delta =
          sum(supplements, 'price') - sum(room.optionalPromos, 'price');
        const deltaInDollar =
          sum(supplements, 'priceInDollar') -
          sum(room.optionalPromos, 'priceInDollar');

        return {
          ...room,
          optionalPromos: supplements,
          bed: {
            ...room.bed,
            totalPriceWithPromo:
              (Number(room.bed?.totalPriceWithPromo) || 0) + delta,
            totalPriceInDollar: {
              ...room.bed?.totalPriceInDollar,
              withPromo:
                (Number(room.bed?.totalPriceInDollar?.withPromo) || 0) +
                deltaInDollar,
            },
          },
        };
      }),
    );
  };

  // KD-3496 — the nights an early check-in / late check-out needs outside the
  // booked window. Shown before the rooms are confirmed, because KOOB does not
  // reserve them: whoever books has to arrange them separately, and finding out
  // afterwards is exactly what this is meant to prevent. `selectedRooms` holds
  // the rooms already validated and `optionalPromo` the supplements ticked on
  // the one still being chosen.
  const stayFlexibleRequirements = getStayFlexibleRequirements(
    [
      ...(selectedRooms ?? []),
      ...(currentSelectedRoom ? [{ optionalPromos: optionalPromo }] : []),
    ],
    { startAt: filters?.startAt, endAt: filters?.endAt },
  );

  const modifyCurrentRooms = (selectionIndex) => {
    const roomSelected = selectedRooms[selectionIndex];
    const newRoomsSelected = selectedRooms.slice(0, selectionIndex);

    setSelectedRooms(newRoomsSelected);
    setCurrentSelectedRoomIndex(roomSelected.roomIndex);
    setCurrentSelectedBedIndex(roomSelected.bedIndex);
    setCurrentSelectedPossibilityIndex(roomSelected.roomIndex);
    // The room goes back to being the pending one, so its supplements go back
    // to being the pending selection — otherwise it re-enters the tunnel with
    // whatever was last ticked elsewhere.
    setOptionalPromo(roomSelected.optionalPromos ?? []);
  };

  const updateRoomFromPromotion = (
    originalIndex,
    bedChoicePromo,
    bedName,
    selectedPossibility,
  ) => {
    const room = initialPossibilities
      .filter((possibility) => {
        return possibility.originalIndex === originalIndex;
      })
      .pop();

    const rooms = initialPossibilities.filter((possibility) => {
      return (
        room.supplierSpecificInformations.hotelRoomId ===
        possibility?.supplierSpecificInformations?.hotelRoomId
      );
    });

    let found = false;
    for (let possibility of rooms) {
      if (found) {
        break;
      }
      for (let i = 0; i < possibility.bedChoices.length; i++) {
        let bed = possibility.bedChoices[i];
        if (
          bed.name === bedName &&
          bed.promotions.join(', ') === bedChoicePromo
        ) {
          initialPossibilities[possibility.originalIndex].allPromotions =
            selectedPossibility.allPromotions;
          setCurrentSelectedRoomIndex(possibility.originalIndex);
          setCurrentSelectedBedIndex(i);
          found = true;
          break;
        }
      }
    }
  };

  // Own inventory exposes minCapacity/maxCapacity instead of nested
  // compositions: for each room slot only offer rooms whose capacity range fits
  // that slot's occupancy. Rooms without capacity fields (third-party) always
  // pass, so their nested-composition flow is unaffected. Once an own-inventory
  // room is picked the whole stay stays own-inventory, so drop other suppliers
  // (mirrors the supplier-locked nested compositions of Koedia/Staah/Rakuten).
  const isOwnInventorySelected =
    selectedRooms?.[0]?.supplier === SupplierEnum.KOOB;
  const currentSlot = rooms?.[selectedRooms.length];
  const currentSlotPax =
    (currentSlot?.adults ?? 0) + (currentSlot?.childrenBirthdates?.length ?? 0);
  const roomFitsCurrentSlot = (room) => {
    if (isOwnInventorySelected && room?.supplier !== SupplierEnum.KOOB) {
      return false;
    }
    if (!currentSlotPax) return true;
    const min = room?.minCapacity ?? 0;
    const max = room?.maxCapacity ?? Infinity;
    return currentSlotPax >= min && currentSlotPax <= max;
  };
  const displayedPossibilities = possibilities.filter(roomFitsCurrentSlot);
  const formattedPossibilities = formatHotelRoomPossibilities(
    displayedPossibilities,
  );

  return (
    <Stack gutterSize={2}>
      <Stack direction="column" style={{ flex: 2, minWidth: 0 }} gutterSize={1}>
        <SectionTitle>
          {isOtherProposal ? t('selectAnOtherRoom') : t('availableRooms')}
        </SectionTitle>

        <Content>
          {isLoading && possibilities?.length === 0 && (
            <KoobLoaderText
              title={t('hotel:loadingRooms.title')}
              description={t('hotel:loadingRooms.description')}
            />
          )}

          {isLoading && (
            <div className="mt-3 flex-col space-y-5">
              <KoobPlaceholder
                count={Math.max(0, 3 - possibilities?.length)}
                className="h-[220px] w-full rounded"
              />
            </div>
          )}

          {!isLoading && formattedPossibilities && (
            <Stack direction="column" size="full" gutterSize={1}>
              {formattedPossibilities.map((possibility, index) => {
                return (
                  <RoomComponent
                    key={'roomComponent_' + index}
                    room={possibility}
                    selectedPossibilityIndex={currentSelectedRoomIndex}
                    isSelected={
                      currentSelectedRoomIndex === possibility.originalIndex ||
                      possibility.originalIndex ===
                        currentSelectedPossibilityIndex ||
                      possibility.variants
                        ?.map((variant) => variant.originalIndex)
                        .includes(currentSelectedRoomIndex)
                    }
                    bedIndex={
                      currentSelectedRoomIndex === possibility.originalIndex
                        ? currentSelectedBedIndex
                        : 0
                    }
                    updateSelectedRoom={handleRoomIndexChange}
                    numberOfNights={numberOfNights}
                    setCurrentSelectedPossibilityIndex={
                      setCurrentSelectedPossibilityIndex
                    }
                    selectedRoomsCount={selectedRooms?.length}
                    bookedRoom={bookedRoom}
                  />
                );
              })}
            </Stack>
          )}

          {!isLoading && displayedPossibilities.length === 0 && (
            <Stack paddingTop={4} alignX="center">
              <Text color="textLight">{t('noPossibilities')}</Text>
            </Stack>
          )}
        </Content>
      </Stack>

      <Stack
        direction="column"
        style={{ flex: 1, minWidth: 0 }}
        gutterSize={1.5}
      >
        {!isOtherProposal && (
          <DmcBookingHotelInfo filters={filters} hotel={hotel} />
        )}

        <DmcBookingHotelRoomsInfo
          rooms={rooms}
          numberOfNights={numberOfNights}
          roomsForRecap={roomsForRecap()}
          modifyCurrentRooms={modifyCurrentRooms}
          setCurrentSelectedBedIndex={setCurrentSelectedBedIndex}
          currentSelectedBedIndex={currentSelectedBedIndex}
          updateRoomFromPromotion={updateRoomFromPromotion}
          optionalPromo={optionalPromo}
          setOptionalPromo={setOptionalPromo}
          selectedRooms={selectedRooms}
          setSelectedPromo={setSelectedPromo}
          handleProposalRoom={handleProposalRoom}
          updateRoomSupplements={updateRoomSupplements}
          currentPossibility={currentSelectedRoom}
        />

        {/*
          Outside the `!isOtherProposal` block on purpose: swapping a room for
          another proposal is a room selection like any other and can carry an
          early check-in / late check-out, so the extra room-night has to be
          stated there too. Its confirm button lives in the parent modal's
          footer, which is why the note sits above the recap rather than above a
          button that only exists on the standard path.
        */}
        <StayFlexibleRequirements
          requirements={stayFlexibleRequirements}
          currency={currency}
          translationKey="bookings"
          keyPrefix="stayFlexible"
        />

        {!isOtherProposal && (
          <>
            <Stack direction="column" gutterSize={1} size="full">
              {!isBookingOnRequest && !isLastRoomOnRequest && (
                <Stack gutterSize={0.5}>
                  <Text color="textLighter">
                    <i
                      className="fa-light fa-triangle-exclamation"
                      style={{ fontSize: 24 }}
                    />
                  </Text>
                  <Text color="textLighter">{t('onRequest')}</Text>
                </Stack>
              )}

              <KoobButton
                type="button"
                variant="primary"
                size="lg"
                block
                disabled={
                  Boolean(filters?.viewAs?.value) ||
                  displayedPossibilities.length === 0 ||
                  !currentSelectedRoom
                }
                onClick={basketHotel ? handleChooseRoom : validateRoom}
                testId="confirm-room"
              >
                {filters?.viewAs?.value
                  ? t('availabilityExplain:readOnly')
                  : basketHotel
                    ? t('next')
                    : selectedRooms?.length === rooms?.length - 1
                      ? t('confirmRooms')
                      : t('nextRoom')}
              </KoobButton>
            </Stack>
            <Stack
              size="full"
              alignY="baseline"
              alignX="space-between"
              gutterSize={1}
            >
              <div className="whitespace-nowrap">
                <Text type="h3" color="textLighter" fontWeight={400}>
                  {t('totalPrice')}
                </Text>
              </div>

              {/* KD-3722 — USD + local currency can be wider than the column: wrap. */}
              <div className="flex flex-wrap justify-end gap-x-1 min-w-0 whitespace-nowrap">
                {!isNaN(totalPriceInDollar) && totalPriceInDollar !== 0 && (
                  <Text type="h3" color="primary" fontWeight={500}>
                    {formatCurrency({
                      amount: totalPriceInDollar,
                      currency: 'usd',
                    })}
                    {' / '}
                  </Text>
                )}

                {totalPrice !== 0 ? (
                  <Text type="h3" color="primary" fontWeight={500}>
                    {formatCurrency({
                      amount: totalPrice,
                      currency: currency,
                    })}
                  </Text>
                ) : (
                  <Text type="h2" color="textLighter" fontWeight={400}>
                    -
                  </Text>
                )}
              </div>
            </Stack>
          </>
        )}
      </Stack>
    </Stack>
  );
};

export default SelectRoomsForm;
